import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { analytics } from "../../analytics/analytics";
import { api } from "../../api/client";
import { Button, Icon } from "../../components/ui";
import {
  interests,
  type DurationPreset,
  type GenerationMode,
  type Interest,
  type PlanInput,
} from "../../domain/models";
import { cities } from "../../fixtures/data";
import { t } from "../../i18n";
import { currentOrigin } from "../../platform/device";
import { useAppStore } from "../../state/appStore";
import { applyTimeTheme, timeThemeFor } from "../../theme/timeTheme";

const durations: Record<DurationPreset, number> = {
  one_to_two_hours: 120,
  three_to_four_hours: 240,
  half_day: 300,
  full_day: 540,
};
const budgetMaximumDollars = 300;

export function HomePage() {
  const navigate = useNavigate();
  const {
    draft,
    setDraft,
    setResult,
    anonymousSessionId,
    authenticated,
    setOrigin,
    useCity: selectCity,
    originLabel,
  } = useAppStore();
  const [step, setStep] = useState<1 | 2>(1);
  const [locationError, setLocationError] = useState("");
  const mutation = useMutation({
    mutationFn: (input: PlanInput) =>
      api.generatePlan(input, anonymousSessionId, !authenticated),
    onSuccess: (result, input) => {
      setResult(result);
      analytics.track("plan_generated", {
        mode: input.generationMode,
        durationMinutes: input.availableDurationMinutes,
        interests: input.interests.length,
      });
      navigate("/plan-results");
    },
  });
  async function locate() {
    setLocationError("");
    try {
      const origin = await currentOrigin();
      setOrigin(origin, origin.label);
    } catch {
      setLocationError(t.home.locationDenied);
    }
  }
  function toggleInterest(interest: Interest) {
    setDraft({
      interests: draft.interests.includes(interest)
        ? draft.interests.filter((item) => item !== interest)
        : [...draft.interests, interest],
    });
  }
  function generate(mode: GenerationMode) {
    setDraft({ generationMode: mode });
    analytics.track(
      mode === "surprise" ? "surprise_plan_started" : "plan_started",
      { durationMinutes: draft.availableDurationMinutes },
    );
    mutation.mutate({ ...draft, generationMode: mode });
  }
  const groupSize = draft.adults + draft.children;
  const budgetDollars = (draft.maximumPerPersonBudgetMinor ?? 0) / 100;
  function changeGroupSize(value: number) {
    setDraft({ adults: value, children: 0, childDetails: [] });
  }
  function changeBudget(value: number) {
    setDraft({
      maximumPerPersonBudgetMinor: value === 0 ? undefined : value * 100,
    });
  }

  return (
    <div className="home-page" data-time-theme={timeThemeFor(draft.startTime)}>
      <div className="hero">
        <div className="top-brand">
          <span className="brand-mark">S</span>
          <strong>{t.appName}</strong>
          <button
            className="icon-button notification-button"
            onClick={() => navigate("/notifications")}
            aria-label="Notifications, 2 unread"
          >
            <Icon name="bell" />
            <span className="notification-badge" aria-hidden>
              2
            </span>
          </button>
        </div>
        <span className="eyebrow">{t.home.eyebrow}</span>
        <h1>{t.home.title}</h1>
        <p>{t.tagline}</p>
      </div>
      <form
        className={`planner planner--step-${step}`}
        onSubmit={(event) => {
          event.preventDefault();
          if (step === 1) {
            setStep(2);
            return;
          }
          generate("standard");
        }}
      >
        <WizardProgress step={step} />
        {step === 1 ? (
          <>
            <fieldset>
              <legend>{t.home.location}</legend>
              <label className="sr-only" htmlFor="plan-city">
                {t.home.city}
              </label>
              <select
                id="plan-city"
                value={draft.cityId ?? ""}
                onChange={(event) => selectCity(event.target.value)}
              >
                <option value="" disabled>
                  {originLabel}
                </option>
                {cities.map((city) => (
                  <option value={city.id} key={city.id}>
                    {city.name} · {city.region}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="location-button"
                onClick={() => void locate()}
              >
                <Icon name="location" />
                {t.home.currentLocation}
              </button>
              <small className="field-hint">{t.home.originHint}</small>
              {locationError && (
                <p className="field-error" role="alert">
                  {locationError}
                </p>
              )}
            </fieldset>
            <div className="form-grid">
              <label>
                {t.home.date}
                <input
                  type="date"
                  min={new Date().toISOString().slice(0, 10)}
                  value={draft.date}
                  onChange={(event) => setDraft({ date: event.target.value })}
                />
              </label>
              <label>
                {t.home.time}
                <input
                  type="time"
                  value={draft.startTime}
                  onInput={(event) => {
                    const startTime = event.currentTarget.value;
                    applyTimeTheme(startTime);
                    setDraft({ startTime });
                  }}
                />
                <small className="field-hint">{t.home.timeThemeHint}</small>
              </label>
            </div>
            <fieldset>
              <legend>{t.home.groupSize}</legend>
              <Counter
                label={t.home.people}
                value={groupSize}
                minimum={1}
                onChange={changeGroupSize}
              />
            </fieldset>
            <fieldset className="budget-slider-field">
              <div className="range-heading">
                <legend>{t.home.budget}</legend>
                <output htmlFor="plan-budget">
                  {budgetDollars === 0 ? t.home.flexible : `$${budgetDollars}`}
                </output>
              </div>
              <input
                id="plan-budget"
                className="budget-slider"
                type="range"
                min="0"
                max={budgetMaximumDollars}
                step="25"
                value={budgetDollars}
                onChange={(event) => changeBudget(Number(event.target.value))}
                aria-label={t.home.budget}
                aria-valuetext={
                  budgetDollars === 0
                    ? t.home.flexible
                    : `$${budgetDollars} per person`
                }
              />
              <div className="range-scale" aria-hidden>
                <span>{t.home.flexible}</span>
                <span>$300</span>
              </div>
              <small className="field-hint">{t.home.budgetHint}</small>
            </fieldset>
            <Button type="submit">
              {t.home.next}
              <span aria-hidden>→</span>
            </Button>
            <button
              type="button"
              className="skip-action"
              onClick={() => generate("standard")}
              disabled={mutation.isPending}
            >
              {t.home.skipPreferences}
            </button>
            {mutation.isError && (
              <p className="field-error" role="alert">
                {t.home.generateError}
              </p>
            )}
          </>
        ) : (
          <>
            <button
              type="button"
              className="wizard-back"
              onClick={() => setStep(1)}
            >
              <span aria-hidden>←</span>
              {t.home.back}
            </button>
            <div className="optional-step-note">
              <strong>{t.home.optionalTitle}</strong>
              <p>{t.home.optionalBody}</p>
            </div>
            <fieldset>
              <legend>
                {t.home.duration}
                <small>{t.home.optional}</small>
              </legend>
              <div className="choice-grid duration-grid">
                {(Object.keys(durations) as DurationPreset[]).map((preset) => (
                  <button
                    type="button"
                    key={preset}
                    className={
                      draft.availableDurationMinutes === durations[preset]
                        ? "selected"
                        : ""
                    }
                    onClick={() =>
                      setDraft({ availableDurationMinutes: durations[preset] })
                    }
                    aria-pressed={
                      draft.availableDurationMinutes === durations[preset]
                    }
                  >
                    {t.durations[preset]}
                  </button>
                ))}
              </div>
            </fieldset>
            <fieldset>
              <legend>
                {t.home.vibe}
                <small>{t.home.optional}</small>
              </legend>
              <div className="interest-grid">
                {interests.map((interest) => (
                  <button
                    type="button"
                    key={interest}
                    className={
                      draft.interests.includes(interest) ? "selected" : ""
                    }
                    onClick={() => toggleInterest(interest)}
                    aria-pressed={draft.interests.includes(interest)}
                  >
                    <span aria-hidden>
                      {interest === "family_friendly"
                        ? "☀"
                        : interest === "party_dancing"
                          ? "♫"
                          : interest === "outdoor"
                            ? "♧"
                            : "◌"}
                    </span>
                    {t.interests[interest]}
                  </button>
                ))}
              </div>
            </fieldset>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? t.states.loading : t.home.generate}
              <span aria-hidden>→</span>
            </Button>
            <button
              type="button"
              className="surprise-action"
              onClick={() => generate("surprise")}
              disabled={mutation.isPending}
            >
              <span aria-hidden>✦</span>
              <span>
                <strong>{t.home.surprise}</strong>
                <small>{t.home.surpriseBody}</small>
              </span>
            </button>
            {mutation.isError && (
              <p className="field-error" role="alert">
                {t.home.generateError}
              </p>
            )}
          </>
        )}
      </form>
    </div>
  );
}

function WizardProgress({ step }: { step: 1 | 2 }) {
  return (
    <div
      className="wizard-progress"
      aria-label={`${t.home.step} ${step} ${t.home.of} 2`}
    >
      <div className="wizard-progress__copy">
        <small>
          {t.home.step} {step} {t.home.of} 2
        </small>
        <strong>{step === 1 ? t.home.stepStart : t.home.stepDetails}</strong>
      </div>
      <div className="wizard-progress__track" aria-hidden>
        <span className="complete" />
        <span className={step === 2 ? "complete" : ""} />
      </div>
    </div>
  );
}
function Counter({
  label,
  value,
  minimum,
  onChange,
}: {
  label: string;
  value: number;
  minimum: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="counter">
      <span>{label}</span>
      <div>
        <button
          type="button"
          onClick={() => onChange(Math.max(minimum, value - 1))}
          aria-label={`Reduce ${label}`}
        >
          −
        </button>
        <strong>{value}</strong>
        <button
          type="button"
          onClick={() => onChange(value + 1)}
          aria-label={`Increase ${label}`}
        >
          +
        </button>
      </div>
    </div>
  );
}
